import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Flame, TrendingUp, TrendingDown, Clock, Award, Lock } from 'lucide-react';
import type { LearningPath } from '@shared/types/learning-path';
import type { ContentDocument } from '@shared/types/content';
import type { CogniPrintProfile } from '@shared/types/cogniprint';
import type { SubscriptionTier } from '@shared/constants/tiers';
import { Button } from '../ui/Button';
import { THICK_TILE_STYLE, THICK_TILE_CLASSNAME } from './tileStyles';
import { aggregateConcepts, weeklyVelocity } from '../../lib/mastery';

interface MasteryDashboardProps {
  tier: SubscriptionTier;
  paths: LearningPath[];
  content: ContentDocument[];
  profile: CogniPrintProfile | null;
}

function scoreBand(score: number): { colour: string; bg: string; border: string; label: string } {
  if (score >= 80) return { colour: '#55EFC4', bg: 'rgba(85,239,196,0.12)', border: 'rgba(85,239,196,0.35)', label: 'Mastered' };
  if (score >= 50) return { colour: '#F0A500', bg: 'rgba(240,165,0,0.12)', border: 'rgba(240,165,0,0.35)', label: 'Developing' };
  return { colour: '#FD79A8', bg: 'rgba(253,121,168,0.12)', border: 'rgba(253,121,168,0.35)', label: 'Needs work' };
}

function formatTime(minutes: number): string {
  if (minutes < 1) return '—';
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = Math.floor(minutes / 60);
  const rem = Math.round(minutes % 60);
  return rem === 0 ? `${hours}h` : `${hours}h ${rem}m`;
}

const CARD_STYLE = { background: '#181D35', borderColor: '#2A2F4F' };

/** At or above this, a concept counts as mastered and never shows as "needs attention". */
const MASTERY_THRESHOLD = 80;

export function MasteryDashboard({ tier, paths, content, profile }: MasteryDashboardProps) {
  const isFree = tier === 'free';
  const isLearner = tier === 'learner';
  const isPro = tier === 'pro' || tier === 'teams';

  const data = useMemo(() => {
    const concepts = aggregateConcepts(paths, content);
    const sorted = [...concepts].sort((a, b) => b.score - a.score);
    const totalMinutes = paths.reduce((sum, p) => sum + (p.progress?.totalTimeSpent ?? 0), 0);
    const overall = concepts.length === 0
      ? 0
      : concepts.reduce((sum, c) => sum + c.score, 0) / concepts.length;
    return {
      concepts,
      strongest: sorted.slice(0, 3),
      // "Needs attention" means genuinely below mastery — not merely "not 100%".
      // The old `< 100` filter put 98%-scored concepts in both columns at once,
      // so the same three items appeared as strongest AND as needing work.
      weakest: [...sorted].reverse().filter((c) => c.score < MASTERY_THRESHOLD).slice(0, 3),
      totalMinutes,
      overall,
      velocity: weeklyVelocity(concepts),
    };
  }, [paths, content]);

  // Free tier → locked preview
  if (isFree) {
    return (
      <div className={`${THICK_TILE_CLASSNAME} p-5 h-full`} style={THICK_TILE_STYLE}>
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/[0.04] to-gold-500/[0.04] pointer-events-none" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={16} className="text-content-dim" />
            <h3 className="font-heading font-semibold text-base">Mastery Dashboard</h3>
          </div>
          <p className="text-xs text-content-secondary font-body mb-4 max-w-md leading-relaxed">
            Track your mastery across concepts, see your learning velocity week over week, and spot the areas where you're strongest. Available on all paid plans.
          </p>
          <Link to="/pricing">
            <Button size="sm" variant="gold">Upgrade to unlock</Button>
          </Link>
        </div>
      </div>
    );
  }

  // Paid but no data yet
  if (data.concepts.length === 0 && data.totalMinutes === 0) {
    return (
      <div className={`${THICK_TILE_CLASSNAME} p-5 h-full`} style={THICK_TILE_STYLE}>
        <h3 className="font-heading font-semibold text-base mb-2">Mastery Dashboard</h3>
        <p className="text-xs text-content-secondary font-body">
          Complete a checkpoint in any learning path to start seeing your mastery metrics here.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className={`${THICK_TILE_CLASSNAME} p-5 h-full space-y-4`}
      style={THICK_TILE_STYLE}
    >
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold text-base">Mastery Dashboard</h3>
        <Link
          to="/profile/evolution"
          className="text-xs text-indigo-400 hover:text-indigo-300 font-body inline-flex items-center gap-1"
        >
          Profile Evolution <ArrowRight size={12} />
        </Link>
      </div>

      {/* Top row — summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        {/* Overall mastery */}
        <div className="p-4 rounded-xl border" style={CARD_STYLE}>
          <div className="flex items-center gap-2 mb-2">
            <Flame size={14} className="text-gold-500" />
            <span className="text-[10px] text-content-dim font-body uppercase tracking-wider">Overall</span>
          </div>
          <div className="text-2xl font-heading font-bold">{Math.round(data.overall)}%</div>
          <div className="text-[11px] text-content-secondary font-body">
            mastery across {data.concepts.length} concept{data.concepts.length === 1 ? '' : 's'}
          </div>
        </div>

        {/* Velocity */}
        <div className="p-4 rounded-xl border" style={CARD_STYLE}>
          <div className="flex items-center gap-2 mb-2">
            {data.velocity.trend === 'up' && <TrendingUp size={14} className="text-[#55EFC4]" />}
            {data.velocity.trend === 'down' && <TrendingDown size={14} className="text-[#FD79A8]" />}
            {data.velocity.trend === 'flat' && <TrendingUp size={14} className="text-content-dim" />}
            <span className="text-[10px] text-content-dim font-body uppercase tracking-wider">Velocity</span>
          </div>
          <div className="text-2xl font-heading font-bold">{data.velocity.thisWeek}</div>
          <div className="text-[11px] text-content-secondary font-body">
            mastered this week
            {data.velocity.lastWeek > 0 && (
              <span className="text-content-dim"> · last week {data.velocity.lastWeek}</span>
            )}
          </div>
        </div>

        {/* Time invested */}
        <div className="p-4 rounded-xl border" style={CARD_STYLE}>
          <div className="flex items-center gap-2 mb-2">
            <Clock size={14} className="text-[#0ABFBC]" />
            <span className="text-[10px] text-content-dim font-body uppercase tracking-wider">Time</span>
          </div>
          <div className="text-2xl font-heading font-bold">{formatTime(data.totalMinutes)}</div>
          <div className="text-[11px] text-content-secondary font-body">
            across {paths.length} path{paths.length === 1 ? '' : 's'}
          </div>
        </div>

        {/* Profile version */}
        <div className="p-4 rounded-xl border" style={CARD_STYLE}>
          <div className="flex items-center gap-2 mb-2">
            <Award size={14} className="text-[#F0A500]" />
            <span className="text-[10px] text-content-dim font-body uppercase tracking-wider">Profile</span>
          </div>
          <div className="text-2xl font-heading font-bold">v{profile?.profileVersion ?? 1}</div>
          <Link
            to="/profile/evolution"
            className="text-[11px] text-indigo-400 font-body hover:text-indigo-300 inline-flex items-center gap-1"
          >
            View evolution <ArrowRight size={10} />
          </Link>
        </div>
      </div>

      {/* Strongest & Weakest */}
      {(data.strongest.length > 0 || data.weakest.length > 0) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {data.strongest.length > 0 && (
            <div className="p-4 rounded-xl border" style={CARD_STYLE}>
              <div className="flex items-center gap-2 mb-3">
                <TrendingUp size={12} className="text-[#55EFC4]" />
                <h4 className="text-[10px] text-content-dim font-body uppercase tracking-wider">Strongest areas</h4>
              </div>
              <div className="space-y-2">
                {data.strongest.map((c) => {
                  const band = scoreBand(c.score);
                  return (
                    <div key={`strong-${c.conceptId}`} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-content font-body truncate">{c.name}</span>
                      <span
                        className="text-[10px] font-body font-semibold px-2 py-0.5 rounded-full shrink-0"
                        style={{ color: band.colour, background: band.bg }}
                      >
                        {Math.round(c.score)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {data.weakest.length > 0 && (
            <div className="p-4 rounded-xl border" style={CARD_STYLE}>
              <div className="flex items-center gap-2 mb-3">
                <TrendingDown size={12} className="text-[#FD79A8]" />
                <h4 className="text-[10px] text-content-dim font-body uppercase tracking-wider">Needs attention</h4>
              </div>
              <div className="space-y-2">
                {data.weakest.map((c) => {
                  const band = scoreBand(c.score);
                  return (
                    <div key={`weak-${c.conceptId}`} className="flex items-center justify-between gap-2">
                      <span className="text-xs text-content font-body truncate">{c.name}</span>
                      <span
                        className="text-[10px] font-body font-semibold px-2 py-0.5 rounded-full shrink-0"
                        style={{ color: band.colour, background: band.bg }}
                      >
                        {Math.round(c.score)}%
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Mastery heat map — Pro + Teams only */}
      {isPro && data.concepts.length > 0 && (
        <div className="p-4 rounded-xl border" style={CARD_STYLE}>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-[10px] text-content-dim font-body uppercase tracking-wider">Mastery heat map</h4>
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1 text-[10px] text-content-dim font-body">
                <span className="w-2 h-2 rounded-sm" style={{ background: '#55EFC4' }} /> 80%+
              </span>
              <span className="flex items-center gap-1 text-[10px] text-content-dim font-body">
                <span className="w-2 h-2 rounded-sm" style={{ background: '#F0A500' }} /> 50-79%
              </span>
              <span className="flex items-center gap-1 text-[10px] text-content-dim font-body">
                <span className="w-2 h-2 rounded-sm" style={{ background: '#FD79A8' }} /> &lt;50%
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {data.concepts.slice(0, 20).map((c) => {
              const band = scoreBand(c.score);
              return (
                <div
                  key={`heat-${c.conceptId}`}
                  className="p-3 rounded-lg border text-center"
                  style={{ background: band.bg, borderColor: band.border }}
                  title={`${c.name} — ${band.label}`}
                >
                  <div className="text-[11px] text-content font-body font-medium truncate mb-1">{c.name}</div>
                  <div className="text-sm font-heading font-bold" style={{ color: band.colour }}>
                    {Math.round(c.score)}%
                  </div>
                </div>
              );
            })}
            {data.concepts.length > 20 && (
              <div
                className="p-3 rounded-lg border flex items-center justify-center text-content-dim"
                style={{ background: '#181D35', borderColor: '#2A2F4F' }}
              >
                <span className="text-[10px] font-body">+{data.concepts.length - 20} more</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pro/Teams — link to the full Learning Insights surface (§5.2.5) */}
      {isPro && (
        <Link
          to="/profile/insights"
          className="flex items-center justify-between gap-2 mt-1 px-3 py-2 rounded-xl border text-xs font-body text-content-secondary hover:text-content transition-colors"
          style={CARD_STYLE}
        >
          <span>View full Learning Insights — velocity, tutor patterns &amp; next actions</span>
          <ArrowRight size={14} className="shrink-0" />
        </Link>
      )}

      {/* Learner — hint about Pro heat map */}
      {isLearner && data.concepts.length > 0 && (
        <div
          className="p-3 rounded-xl border flex items-center justify-between gap-3"
          style={CARD_STYLE}
        >
          <div className="flex items-center gap-2">
            <Lock size={14} className="text-content-dim" />
            <span className="text-xs text-content-secondary font-body">
              Unlock the full mastery heat map with Pro.
            </span>
          </div>
          <Link to="/pricing">
            <Button size="sm" variant="secondary">Upgrade</Button>
          </Link>
        </div>
      )}
    </motion.div>
  );
}
